// src/components/ui/Dialog.jsx (or wherever you keep it)
import React, { useEffect, useRef, useId } from 'react';

const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-4xl',
  full: 'max-w-[95vw] md:max-w-[90vw]',
};

const Dialog = ({
  isOpen,
  onClose,
  title,
  children,
  footer,

  // new props
  variant = 'default',            // 'default' | 'custom'
  size = 'lg',                    // 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showHeader = true,              // only for variant='default'
  showFooter = true,              // only for variant='default'
  closeOnEsc = true,
  closeOnOutside = true,

  // style hooks
  bodyClassName = '',
  headerRight = null,             // extra actions in default header
}) => {
  const labelId = useId();
  const modalRef = useRef();

  // esc to close
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handleEsc = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, onClose]);

  // click outside to close
  useEffect(() => {
    if (!isOpen || !closeOnOutside) return;
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeOnOutside, onClose]);

  // lock background scroll
  useEffect(() => {
    if (!isOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? labelId : undefined}
    >
      {/* overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* container to center */}
      <div className="relative z-10 grid min-h-full place-items-center p-4">
        <div
          ref={modalRef}
          className={`w-full ${SIZE_CLASS[size]} rounded-2xl bg-white shadow-xl overflow-hidden`}
        >
          {/* grid keeps header/footer sticky while body scrolls */}
          <div className="grid max-h-[90vh] grid-rows-[auto,1fr,auto]">
            {/* header */}
            {variant === 'default' && showHeader && (
              <div className="flex items-center justify-between border-b px-6 py-4">
                <h3 id={labelId} className="text-lg font-semibold text-gray-800">
                  {title}
                </h3>
                <div className="flex items-center gap-2">
                  {headerRight}
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                    aria-label="Close"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}

            {/* for custom variant: only a top-right close button */}
            {variant === 'custom' && (
              <button
                onClick={onClose}
                className="absolute right-3 top-3 z-20 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                &times;
              </button>
            )}

            {/* body (scrollable) */}
            <div className={`overflow-y-auto ${bodyClassName}`}>
              {children}
            </div>

            {/* footer */}
            {variant === 'default' && showFooter && (
              <div className="flex justify-end gap-2 border-t px-6 py-4">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dialog;
/*footer={
    <>
      <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
      <Button onClick={handleDelete}>Delete</Button>
    </>
  }
    */